import posthog from 'posthog-js';
import type { AnalyticsEventName, AnalyticsEvents, SuperProperties } from './events.js';
import { VERIFY_FLAG_KEY, resolveInitGate } from './gate.js';
import type { InitDecision } from './gate.js';
import { isInternal } from './internal.js';
import { redactCaptureResult, redactUrlString } from './redaction.js';
import type { RedactablePayload } from './redaction.js';

interface CaptureLike extends RedactablePayload {
  event: string;
}

/**
 * Defence in depth behind `mask_personal_data_properties`. The SDK masks the URL
 * it stores but not the referrer alongside it, so this is what closes
 * $referrer / $initial_referrer / $session_entry_referrer. Redaction is
 * deterministic and value-driven (see redaction.ts), so there is no runtime
 * canary layered on top of it; the Task 16 manual search is the end-to-end check.
 *
 * It must never return null: a dropped event is silent data loss, and on the free
 * plan dropped data is gone permanently rather than billed.
 */
export function buildBeforeSend() {
  return (payload: CaptureLike | null): CaptureLike | null =>
    payload ? redactCaptureResult(payload) : payload;
}

interface CapturedNetworkRequest {
  name?: string;
  requestBody?: string | null;
  responseBody?: string | null;
  [key: string]: unknown;
}

// Mirrors posthog-js's own PAYLOAD_CONTENT_DENY_LIST (extensions/replay/external/config.js)
// term for term, plus `authorization`, which is not in posthog's list but is harmless to keep.
const PAYLOAD_SCRUB_PATTERN =
  /("(?:password|secret|passwd|api_key|apikey|auth|credentials|mysql_pwd|privatekey|private_key|token|authorization)"\s*:\s*")[^"]*(")/gi;

/**
 * Despite the name, posthog-js overloads this as the general replay URL mask. It
 * is the only hook that reaches the rrweb Meta event href behind the replay
 * player's URL bar and the recordings-list "visited page" filter.
 *
 * It also covers the typeahead: network performance entries carry the full
 * request URL and are captured by default, so /api/v1/contacts?q=... would
 * otherwise land in the replay network tab on every keystroke.
 */
export function buildMaskNetworkRequestFn() {
  return (request: CapturedNetworkRequest): CapturedNetworkRequest => {
    // Never return null. posthog-js drops the entire Meta event when this
    // returns falsy, taking viewport width and height with it.
    const out: CapturedNetworkRequest = { ...request };
    if (typeof out.name === 'string') out.name = redactUrlString(out.name);
    // Defining this callback replaces PostHog's built-in payload scrubber, so
    // the password/token deny-list has to be reimplemented here.
    if (typeof out.requestBody === 'string') {
      out.requestBody = out.requestBody.replace(PAYLOAD_SCRUB_PATTERN, '$1<masked>$2');
    }
    if (typeof out.responseBody === 'string') {
      out.responseBody = out.responseBody.replace(PAYLOAD_SCRUB_PATTERN, '$1<masked>$2');
    }
    return out;
  };
}

export interface PostHogConfigOptions {
  apiHost: string;
  uiHost: string;
}

export function buildPostHogConfig(
  decision: Extract<InitDecision, { init: true }>,
  options: PostHogConfigOptions,
): Record<string, unknown> {
  return {
    api_host: options.apiHost,
    // Mandatory when proxying, or the toolbar and the replay player break.
    ui_host: options.uiHost,
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    autocapture: true,
    // The primary redaction: masks q wherever posthog-js constructs a URL,
    // including the stored initial-person info and the replay snapshot URL.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['q'],
    // persistence is deliberately absent. The default localStorage+cookie is the
    // only cross-subdomain mechanism, and the marketing-to-app stitch needs it.
    disable_session_recording: !decision.replay,
    session_recording: {
      maskAllInputs: true,
      maskCapturedNetworkRequestFn: buildMaskNetworkRequestFn(),
    },
    before_send: buildBeforeSend(),
  };
}

export interface InitAnalyticsOptions {
  key: string | undefined;
  apiHost: string;
  uiHost: string;
  isProductionBuild: boolean;
  productionHost: string;
  userEmail: string | null | undefined;
  environmentOverride?: never;
}

let initialized = false;
let blocked = false;
let currentEnvironment: 'production' | 'development' = 'production';

/** Idempotent: StrictMode double-invokes effects, and posthog.init is not safe to repeat. */
export function initAnalytics(options: InitAnalyticsOptions): boolean {
  // Terminal, and checked before the idempotency return: once shutdownAnalytics
  // has run, no later call re-enables capture. Signing out flips the email back
  // to null and would otherwise look like a fresh, external visitor.
  if (blocked) return false;
  if (initialized) return true;
  if (typeof window === 'undefined') return false;

  const verifyFlag = window.sessionStorage.getItem(VERIFY_FLAG_KEY) !== null;
  const decision = resolveInitGate({
    key: options.key,
    isProductionBuild: options.isProductionBuild,
    host: window.location.host,
    productionHost: options.productionHost,
    verifyFlag,
    internal: isInternal(options.userEmail),
  });

  if (!decision.init) return false;

  currentEnvironment = decision.environment;
  posthog.init(
    options.key as string,
    buildPostHogConfig(decision, {
      apiHost: options.apiHost,
      uiHost: options.uiHost,
    }) as Parameters<typeof posthog.init>[1],
  );
  posthog.register({ environment: decision.environment, app: 'web' });
  initialized = true;
  return true;
}

export function isAnalyticsActive(): boolean {
  return initialized;
}

/**
 * PostHog returns 200 OK and silently drops an event with an empty distinct_id,
 * so both wrappers hard-guard it.
 */
export function capture<K extends AnalyticsEventName>(
  name: K,
  properties: AnalyticsEvents[K],
  options?: { sendInstantly?: boolean },
): void {
  if (!initialized) return;
  if (!posthog.get_distinct_id()) return;
  posthog.capture(name, properties as Record<string, unknown>, {
    send_instantly: options?.sendInstantly ?? false,
  });
}

export function identify(distinctId: string, personProperties: Record<string, unknown>): void {
  if (!initialized) return;
  if (!distinctId) return;
  posthog.identify(distinctId, personProperties);
}

export function registerSuperProperties(properties: Partial<SuperProperties>): void {
  if (!initialized) return;
  posthog.register({ ...properties, environment: currentEnvironment, app: 'web' });
}

export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}

/**
 * The late internal block. `initAnalytics` is one-shot, so it cannot refuse an
 * employee who signs in on a tab that already initialized anonymously. This is
 * the only way back out. Clearing `initialized` makes every wrapper above a
 * no-op, and `blocked` stops a later init from undoing it.
 *
 * Deliberately not persisted (Spec Deviations item 6): the residual is the
 * handful of anonymous events before the sign-in, and the project-level
 * internal-user filter absorbs those the same way it does on marketing and docs.
 */
export function shutdownAnalytics(): void {
  blocked = true;
  if (!initialized) return;
  initialized = false;
  posthog.stopSessionRecording();
  posthog.reset();
}
